/**
 * MOCK DATA — fixtures for the non-ledger views: ribbon, risk center, research,
 * backtests, operations and the market monitor.
 *
 * See `src/mock/banner.ts`. Every value is synthetic. Where a field references
 * real infrastructure (bridge heartbeat, DB latency, DeepSeek health) the mock
 * value is an *invented but plausible* number, and the UI says so.
 */
import type {
  AllowListEntryView,
  BacktestMetrics,
  BacktestRunView,
  BarView,
  BridgeStatusView,
  DataHealthView,
  FeatureFreshnessRow,
  IndicatorSeriesView,
  QuoteRowView,
  ReadinessCheckView,
  ReadinessView,
  RegimeSnapshotView,
  ResearchAssessmentView,
  ResearchCallCostSummary,
  RibbonAlert,
  RibbonStatus,
  RiskConfigAuditRow,
  RiskLimitRow,
  SchedulerView,
  StrategyEligibilityView,
  TerminalMetadataView,
  Timeframe,
  TradeDecisionTrailView,
} from '../api/types';
import { FRESHNESS_THRESHOLDS } from '../config';
import { mockId, mockUuid, round } from './prng';
import { MOCK_DATASET, MOCK_FEATURE_VERSION, MOCK_INSTRUMENTS, MOCK_REGIME_VERSIONS, type MockInstrument } from './dataset';
import { DAILY_LOSS_CAP_PCT, DEMO_EQUITY_BASE } from './metrics';

const NOW = MOCK_DATASET.now;
const iso = (offsetMs: number): string => new Date(NOW + offsetMs).toISOString();

const instrumentBySymbol = (symbol: string): MockInstrument => {
  const found = MOCK_INSTRUMENTS.find((instrument) => instrument.symbol === symbol);
  if (found === undefined) throw new Error(`unknown mock symbol ${symbol}`);
  return found;
};

/* ------------------------------------------------------------------ *
 * §3.2 A — Status ribbon
 * ------------------------------------------------------------------ */

export function buildRibbon(overrides: Partial<RibbonStatus> = {}): RibbonStatus {
  const alerts: RibbonAlert[] = [
    {
      id: 'alert-spread',
      severity: 'warning',
      message: 'GBPUSD spread is above its typical range; the risk engine is deferring new entries on this symbol.',
      raised_at: iso(-6 * 60_000),
      source: 'risk_engine',
    },
    {
      id: 'alert-research',
      severity: 'info',
      message: 'DeepSeek latency is elevated (p95 above the 2 s budget); research adjustments are being treated as neutral when they expire.',
      raised_at: iso(-22 * 60_000),
      source: 'research_engine',
    },
  ];

  const base: RibbonStatus = {
    as_of: iso(0),
    server_time: iso(0),
    mt5: 'connected',
    mt5_terminal: {
      terminal_build: '4755',
      last_heartbeat_at: iso(-1_400),
      last_reconciliation_at: iso(-4 * 60_000),
      last_reconciliation_ok: true,
    },
    account: {
      mode: 'demo',
      demo_verified: true,
      broker_server: 'MockBroker-Demo01',
      account_number_masked: '•••• 4821',
      account_currency: 'USD',
      margin_mode: 'hedging',
      leverage: 100,
      verified_at: iso(-3 * 3_600_000),
    },
    execution_mode: 'approval_required',
    session: {
      phase: 'active',
      label: 'London / New York overlap',
      broker_timezone: 'Europe/London',
      broker_time: iso(0),
      next_cutoff_at: iso(96 * 60_000),
      flatten_deadline_at: iso(214 * 60_000),
    },
    risk_state: 'warning',
    halt: {
      level: 'none',
      activated_at: null,
      activated_by: null,
      reason: null,
      source: null,
      allows_protective_actions: true,
      allows_new_entries: true,
    },
    data_freshness: {
      tick_age_ms: 1_180,
      bar_age_ms: 46_000,
      clock_drift_ms: 142,
      last_tick_at: iso(-1_180),
      last_bar_at: iso(-46_000),
      last_event_at: iso(-1_180),
      thresholds: FRESHNESS_THRESHOLDS,
    },
    deepseek: {
      health: 'degraded',
      last_ok_at: iso(-9 * 60_000),
      last_latency_ms: 2_640,
      model_id: 'deepseek-chat',
      consecutive_failures: 1,
    },
    readiness: 'degraded',
    alerts,
  };
  return { ...base, ...overrides };
}

/* ------------------------------------------------------------------ *
 * §3.2 D — Risk center
 * ------------------------------------------------------------------ */

export function buildRiskLimits(): RiskLimitRow[] {
  const dailyCap = round((DEMO_EQUITY_BASE * DAILY_LOSS_CAP_PCT) / 100, 2);
  const realizedAndUnrealized = MOCK_DATASET.closed_trades.reduce((total, trade) => total + trade.net_realized_pnl, 0);
  const lossToDate = Math.max(0, -realizedAndUnrealized);
  const grossNotional = MOCK_DATASET.positions.reduce((total, position) => total + position.notional, 0);

  const shared = {
    config_version: 'risk-2026.09.4',
    config_hash: 'sha256:risk-cfg-9f2c41ab',
    editable: true,
    requires_second_confirmation: true,
    hard_ceiling: null,
    note: null,
  } as const;

  return [
    {
      ...shared,
      limit_id: 'daily_loss_limit',
      label: 'Daily loss limit',
      category: 'loss',
      unit: 'percent_equity',
      configured_value: DAILY_LOSS_CAP_PCT,
      configured_display: `${DAILY_LOSS_CAP_PCT.toFixed(1)}% of demo equity (${dailyCap.toFixed(2)} USD)`,
      current_utilization: round((lossToDate / dailyCap) * 100, 2),
      utilization_display: `${lossToDate.toFixed(2)} USD of ${dailyCap.toFixed(2)} USD`,
      utilization_pct: round(lossToDate / dailyCap, 4),
      utilization_trend: 'rising',
      status: lossToDate / dailyCap >= 0.65 ? 'warning' : 'ok',
      breach_action: 'soft_halt',
      last_modified_by: 'operator.ayoub',
      last_modified_at: iso(-26 * 3_600_000),
    },
    {
      ...shared,
      limit_id: 'per_trade_risk_limit',
      label: 'Per-trade risk limit',
      category: 'size',
      unit: 'percent_equity',
      configured_value: 0.5,
      configured_display: '0.50% of demo equity per trade',
      current_utilization: 0.42,
      utilization_display: 'largest open position: 0.42% of equity',
      utilization_pct: 0.84,
      utilization_trend: 'flat',
      status: 'ok',
      breach_action: 'reduce',
      hard_ceiling: 1.0,
      last_modified_by: 'operator.ayoub',
      last_modified_at: iso(-26 * 3_600_000),
    },
    {
      ...shared,
      limit_id: 'max_open_positions',
      label: 'Maximum open positions',
      category: 'exposure',
      unit: 'count',
      configured_value: 3,
      configured_display: '3 positions',
      current_utilization: MOCK_DATASET.positions.length,
      utilization_display: `${MOCK_DATASET.positions.length} of 3 open`,
      utilization_pct: round(MOCK_DATASET.positions.length / 3, 4),
      utilization_trend: 'flat',
      status: MOCK_DATASET.positions.length >= 3 ? 'breach' : 'ok',
      breach_action: 'reject',
      last_modified_by: 'operator.ayoub',
      last_modified_at: iso(-74 * 3_600_000),
    },
    {
      ...shared,
      limit_id: 'max_gross_notional',
      label: 'Maximum gross notional / exposure',
      category: 'exposure',
      unit: 'currency',
      configured_value: 60_000,
      configured_display: '60,000 USD gross',
      current_utilization: round(grossNotional, 2),
      utilization_display: `${grossNotional.toFixed(0)} USD gross`,
      utilization_pct: round(grossNotional / 60_000, 4),
      utilization_trend: 'rising',
      status: grossNotional / 60_000 >= 0.8 ? 'warning' : 'ok',
      breach_action: 'reject',
      last_modified_by: 'operator.ayoub',
      last_modified_at: iso(-74 * 3_600_000),
    },
    {
      ...shared,
      limit_id: 'per_symbol_exposure_cap',
      label: 'Per-symbol exposure cap',
      category: 'exposure',
      unit: 'currency',
      configured_value: 25_000,
      configured_display: '25,000 USD per symbol',
      current_utilization: 9_580,
      utilization_display: 'EURUSD 9,580 USD (highest symbol)',
      utilization_pct: round(9_580 / 25_000, 4),
      utilization_trend: 'flat',
      status: 'ok',
      breach_action: 'reduce',
      last_modified_by: null,
      last_modified_at: null,
    },
    {
      ...shared,
      limit_id: 'max_spread',
      label: 'Maximum spread allowed',
      category: 'execution',
      unit: 'pips',
      configured_value: 1.5,
      configured_display: '1.5 pips (EURUSD), instrument-specific',
      current_utilization: 1.6,
      utilization_display: 'GBPUSD currently 1.6 pips',
      utilization_pct: round(1.6 / 1.5, 4),
      utilization_trend: 'rising',
      status: 'breach',
      breach_action: 'defer',
      last_modified_by: 'operator.ayoub',
      last_modified_at: iso(-5 * 3_600_000),
      note: 'Widened briefly after the London open; the risk engine is deferring GBPUSD entries until spreads normalise.',
    },
    {
      ...shared,
      limit_id: 'max_slippage',
      label: 'Maximum slippage allowed',
      category: 'execution',
      unit: 'points',
      configured_value: 12,
      configured_display: '12 points per fill',
      current_utilization: 6,
      utilization_display: 'worst fill today: 6 points',
      utilization_pct: round(6 / 12, 4),
      utilization_trend: 'falling',
      status: 'ok',
      breach_action: 'reject',
      last_modified_by: null,
      last_modified_at: null,
    },
    {
      ...shared,
      limit_id: 'max_consecutive_losses',
      label: 'Maximum consecutive losses',
      category: 'behaviour',
      unit: 'count',
      configured_value: 3,
      configured_display: '3 losses in a row',
      current_utilization: 1,
      utilization_display: 'current losing streak: 1',
      utilization_pct: round(1 / 3, 4),
      utilization_trend: 'flat',
      status: 'ok',
      breach_action: 'soft_halt',
      last_modified_by: null,
      last_modified_at: null,
    },
    {
      ...shared,
      limit_id: 'flatten_deadline',
      label: 'End-of-session flatten deadline',
      category: 'session',
      unit: 'broker_time',
      configured_value: '20:45',
      configured_display: '15 minutes before the 21:00 broker close',
      current_utilization: null,
      utilization_display: 'next flatten at 20:45 Europe/London',
      utilization_pct: null,
      utilization_trend: null,
      status: 'ok',
      breach_action: 'stop_entries',
      last_modified_by: 'operator.ayoub',
      last_modified_at: iso(-98 * 3_600_000),
    },
    {
      ...shared,
      limit_id: 'halt_state',
      label: 'Manual / automatic halt state',
      category: 'halt',
      unit: 'enum',
      configured_value: 'normal',
      configured_display: 'no halt active',
      current_utilization: null,
      utilization_display: 'last halt: none today',
      utilization_pct: null,
      utilization_trend: null,
      status: 'ok',
      breach_action: 'hard_halt',
      editable: false,
      last_modified_by: null,
      last_modified_at: null,
      note: 'Halt state is changed through the dedicated soft/hard halt controls, never by editing this row.',
    },
  ];
}

/** §3.2 D read-only audit log — operator, timestamp, prior value, new value, reason. */
export function buildRiskAuditLog(): RiskConfigAuditRow[] {
  const rows: RiskConfigAuditRow[] = [
    {
      audit_event_id: mockUuid('audit', 1),
      occurred_at: iso(-5 * 3_600_000),
      operator: 'operator.ayoub',
      target: 'max_spread · EURUSD',
      target_kind: 'risk_limit',
      prior_value: '15 points',
      new_value: '12 points',
      reason: 'London open spreads widened beyond the strategy assumption; tightening the cap so the risk engine defers earlier.',
      config_version_before: 'risk-2026.09.3',
      config_version_after: 'risk-2026.09.4',
      replay_validation: { required: true, performed: true, session_date: 'previous session', result: 'pass' },
      chain_hash: 'sha256:9c41ab7f',
      previous_chain_hash: 'sha256:31d0ee02',
      source_ip: '127.0.0.1',
    },
    {
      audit_event_id: mockUuid('audit', 2),
      occurred_at: iso(-26 * 3_600_000),
      operator: 'operator.ayoub',
      target: 'daily loss limit',
      target_kind: 'risk_limit',
      prior_value: '2.5% of demo equity',
      new_value: '2.0% of demo equity',
      reason: 'Tightening the demo daily cap for the first week of forward testing; no strategy change implied.',
      config_version_before: 'risk-2026.09.2',
      config_version_after: 'risk-2026.09.3',
      replay_validation: { required: true, performed: true, session_date: 'prior session', result: 'pass' },
      chain_hash: 'sha256:31d0ee02',
      previous_chain_hash: 'sha256:77aa10c4',
      source_ip: '127.0.0.1',
    },
    {
      audit_event_id: mockUuid('audit', 3),
      occurred_at: iso(-27 * 3_600_000),
      operator: 'operator.jhart',
      target: 'instrument allow-list · XAUUSD',
      target_kind: 'allow_list',
      prior_value: 'enabled',
      new_value: 'disabled',
      reason: 'Gold swap costs during rollover make the demo results unrepresentative of the strategy assumption.',
      config_version_before: 'risk-2026.09.2',
      config_version_after: 'risk-2026.09.2',
      replay_validation: { required: true, performed: false, session_date: null, result: 'not_run' },
      chain_hash: 'sha256:77aa10c4',
      previous_chain_hash: 'sha256:0b19fe55',
      source_ip: '127.0.0.1',
    },
    {
      audit_event_id: mockUuid('audit', 4),
      occurred_at: iso(-74 * 3_600_000),
      operator: 'operator.ayoub',
      target: 'max open positions',
      target_kind: 'risk_limit',
      prior_value: '2 positions',
      new_value: '3 positions',
      reason: 'Portfolio design review: three uncorrelated instruments with a 0.4/0.3/0.3 allocation split.',
      config_version_before: 'risk-2026.09.1',
      config_version_after: 'risk-2026.09.2',
      replay_validation: { required: true, performed: true, session_date: 'walk-forward window 4', result: 'pass' },
      chain_hash: 'sha256:0b19fe55',
      previous_chain_hash: null,
      source_ip: '127.0.0.1',
    },
    {
      audit_event_id: mockUuid('audit', 5),
      occurred_at: iso(-98 * 3_600_000),
      operator: 'operator.ayoub',
      target: 'execution_mode',
      target_kind: 'execution_mode',
      prior_value: 'disabled',
      new_value: 'approval_required',
      reason: 'Phase 3 gate: demo execution enabled with manual approval of every order.',
      config_version_before: 'risk-2026.09.1',
      config_version_after: 'risk-2026.09.1',
      replay_validation: { required: false, performed: false, session_date: null, result: null },
      chain_hash: 'sha256:5de1c0aa',
      previous_chain_hash: null,
      source_ip: '127.0.0.1',
    },
  ];
  return rows;
}

export function buildAllowLists(): AllowListEntryView[] {
  return [
    { list_kind: 'instrument', entry_id: 'al-eurusd', value: 'EURUSD', enabled: true, note: 'Primary instrument; tightest spread of the allowed set.', last_modified_by: 'operator.ayoub', last_modified_at: iso(-98 * 3_600_000) },
    { list_kind: 'instrument', entry_id: 'al-gbpusd', value: 'GBPUSD', enabled: true, note: 'Enabled; entries deferred while the spread cap is breached.', last_modified_by: 'operator.ayoub', last_modified_at: iso(-98 * 3_600_000) },
    { list_kind: 'instrument', entry_id: 'al-usdjpy', value: 'USDJPY', enabled: true, note: null, last_modified_by: 'operator.ayoub', last_modified_at: iso(-74 * 3_600_000) },
    { list_kind: 'instrument', entry_id: 'al-xauusd', value: 'XAUUSD', enabled: false, note: 'Disabled: rollover swap makes demo results unrepresentative.', last_modified_by: 'operator.jhart', last_modified_at: iso(-27 * 3_600_000) },
    { list_kind: 'session_window', entry_id: 'al-london-ny', value: '08:00–16:30 Europe/London', enabled: true, note: 'London / New York overlap; no entries after the 20:45 flatten deadline.', last_modified_by: 'operator.ayoub', last_modified_at: iso(-98 * 3_600_000) },
  ];
}

/* ------------------------------------------------------------------ *
 * §3.2 E — Strategy & AI research
 * ------------------------------------------------------------------ */

export function buildStrategies(): StrategyEligibilityView[] {
  return [
    {
      strategy_id: 'orb-v1',
      version: 'orb-v1.0.0',
      display_name: 'Opening-range breakout',
      enabled: true,
      intended_regime: 'Trending session opens with expanding range and above-average relative volume.',
      entry_rules: [
        'Break of the 15-minute opening range in the direction of the first impulse.',
        'Requires relative volume ≥ 1.2 and ADX-derived regime = trending.',
        'No entry during the first 15 minutes after the session open (gap-risk warm-up).',
      ],
      exit_rules: ['Stop at the opposite side of the opening range.', 'Target at 1.5× initial risk.', 'Time exit after 180 minutes.'],
      eligibility: {
        session_phase: ['opening'],
        regime: ['trending'],
        volatility_bucket: ['normal', 'high'],
        symbols: ['EURUSD', 'GBPUSD'],
        min_minutes_after_open: 15,
        max_spread_points: 12,
      },
      risk_allocation: { share_of_daily_budget: 0.4, policy: 'static_human_reviewed' },
      model_reference: { feature_version: MOCK_FEATURE_VERSION, model_version: 'deterministic-rules-1.0', config_hash: 'sha256:orb-cfg-1a2b' },
      health: {
        trades: 11,
        win_rate: 0.545,
        expectancy: 14.62,
        profit_factor: 1.42,
        max_drawdown: 186.4,
        average_r_multiple: 0.38,
        uptime_pct: 0.998,
        missed_trade_causes: [
          { cause: 'spread above cap', count: 7 },
          { cause: 'research veto', count: 2 },
          { cause: 'daily loss budget exhausted', count: 1 },
        ],
        last_signal_at: iso(-14 * 60_000),
      },
      eligible_now: true,
      ineligible_reason: null,
      config_version: 'strategies-2026.09.2',
    },
    {
      strategy_id: 'vwap-reversion-v1',
      version: 'vwap-reversion-v1.0.0',
      display_name: 'VWAP mean reversion',
      enabled: true,
      intended_regime: 'Ranging mid-session conditions with price stretched from session VWAP.',
      entry_rules: ['Fade a ≥ 2σ deviation from session VWAP back toward VWAP.', 'Regime must be ranging; volatility bucket normal.'],
      exit_rules: ['Target at session VWAP.', 'Stop at 3σ deviation.', 'Time exit into the pre-close window.'],
      eligibility: {
        session_phase: ['mid_session'],
        regime: ['ranging'],
        volatility_bucket: ['low', 'normal'],
        symbols: ['EURUSD', 'USDJPY'],
        min_minutes_after_open: null,
        max_spread_points: 12,
      },
      risk_allocation: { share_of_daily_budget: 0.3, policy: 'static_human_reviewed' },
      model_reference: { feature_version: MOCK_FEATURE_VERSION, model_version: 'deterministic-rules-1.0', config_hash: 'sha256:vwap-cfg-77c1' },
      health: {
        trades: 9,
        win_rate: 0.667,
        expectancy: 9.18,
        profit_factor: 1.31,
        max_drawdown: 214.8,
        average_r_multiple: 0.24,
        uptime_pct: 0.994,
        missed_trade_causes: [
          { cause: 'regime not ranging', count: 11 },
          { cause: 'session phase ineligible', count: 5 },
        ],
        last_signal_at: iso(-38 * 60_000),
      },
      eligible_now: true,
      ineligible_reason: null,
      config_version: 'strategies-2026.09.2',
    },
    {
      strategy_id: 'momentum-continuation-v1',
      version: 'momentum-continuation-v1.0.0',
      display_name: 'Momentum continuation',
      enabled: false,
      intended_regime: 'Strong directional continuation after a news-driven impulse.',
      entry_rules: ['Enter on a pullback that holds above the impulse midpoint.', 'Regime must be trending with high volatility.'],
      exit_rules: ['Trail the stop behind the last swing.', 'Target at 2× initial risk.', 'Flatten at the session cutoff.'],
      eligibility: {
        session_phase: ['mid_session', 'pre_close'],
        regime: ['trending'],
        volatility_bucket: ['high'],
        symbols: ['EURUSD', 'GBPUSD', 'USDJPY'],
        min_minutes_after_open: 30,
        max_spread_points: 15,
      },
      risk_allocation: { share_of_daily_budget: 0.3, policy: 'static_human_reviewed' },
      model_reference: { feature_version: MOCK_FEATURE_VERSION, model_version: 'deterministic-rules-1.0', config_hash: 'sha256:mom-cfg-3d9e' },
      health: {
        trades: 0,
        win_rate: null,
        expectancy: null,
        profit_factor: null,
        max_drawdown: null,
        average_r_multiple: null,
        uptime_pct: null,
        missed_trade_causes: [{ cause: 'strategy disabled pending walk-forward validation', count: 1 }],
        last_signal_at: null,
      },
      eligible_now: false,
      ineligible_reason: 'Disabled: awaiting walk-forward validation before it is allocated live demo budget (§18.7).',
      config_version: 'strategies-2026.09.2',
    },
  ];
}

/** Pre-session assessments plus the per-decision ones from the ledger. */
export function buildResearchAssessments(): ResearchAssessmentView[] {
  const preSession: ResearchAssessmentView[] = MOCK_INSTRUMENTS.slice(0, 3).map((instrument, index) => ({
    assessment_id: mockUuid('ra-pre', index),
    decision_id: null,
    symbol: instrument.symbol,
    as_of: iso(-95 * 60_000),
    received_at: iso(-93 * 60_000),
    coverage_status: index === 2 ? 'insufficient' : 'sufficient',
    event_direction: index === 0 ? 'neutral' : index === 1 ? 'bullish' : 'mixed',
    event_relevance: round(0.2 + index * 0.25, 3),
    confidence: round(0.4 + index * 0.2, 3),
    event_horizon_minutes: 240,
    risk_flags:
      index === 2
        ? [{ type: 'scheduled_macro_event', severity: 'high', evidence_ids: [mockId('doc', 40 + index)], note: 'Central bank decision inside the session window.' }]
        : [],
    evidence_ids: [mockId('doc', 40 + index), mockId('doc', 50 + index)],
    counter_evidence_ids: index === 1 ? [mockId('doc', 60 + index)] : [],
    research_score: round(-0.2 + index * 0.3, 3),
    recommendation: index === 2 ? 'veto_quant_signal' : 'allow_quant_signal',
    rationale: 'Pre-session evidence bundle reviewed before the instrument became eligible. Stored with source hashes and a prompt version so the assessment can be reproduced.',
    reasoning_steps: [
      { claim: 'The overnight calendar contains one high-severity scheduled event for this instrument.', evidence_ids: [mockId('doc', 40 + index)] },
      { claim: 'Liquidity conditions in the supplied snapshots were normal at the time of assessment.', evidence_ids: [mockId('doc', 50 + index)] },
    ],
    conviction_tier: index === 2 ? 'low' : 'medium',
    proposed_stop_adjustment_pct: 0,
    proposed_target_adjustment_pct: 0,
    schema_validation: { status: 'valid', schema_version: '2.0', errors: [], raw_payload_ref: null, validated_at: iso(-93 * 60_000) },
    prompt_version: 'prompt-research-2026.09-v2',
    model_version: 'deepseek-chat-2026-09',
    model_id: 'deepseek-chat',
    call: {
      llm_call_id: mockUuid('llm-pre', index),
      started_at: iso(-95 * 60_000),
      latency_ms: 1_240 + index * 320,
      prompt_tokens: 3_100 + index * 220,
      completion_tokens: 410 + index * 60,
      cost_usd: round(0.0021 + index * 0.0004, 5),
      status: 'ok',
      attempt: 1,
    },
    applied_adjustment: {
      research_adjustment: 0,
      event_penalty: index === 2 ? -0.15 : 0,
      clamp_min: -0.1,
      clamp_max: 0.1,
      clamped: false,
      conviction_multiplier: null,
    },
    staleness_deadline_at: iso(60 * 60_000),
    expired: false,
  }));

  const perDecision = Object.values(MOCK_DATASET.decision_artifacts).map((artifacts) => artifacts.research);
  return [...preSession, ...perDecision].sort((left, right) => right.received_at.localeCompare(left.received_at));
}

export function buildFeatureFreshness(): FeatureFreshnessRow[] {
  return MOCK_INSTRUMENTS.flatMap((instrument, instrumentIndex) =>
    [
      { name: 'atr14', cadence: 300_000, age: 46_000 },
      { name: 'vwap_deviation_sigma', cadence: 60_000, age: 22_000 + instrumentIndex * 4_000 },
      { name: 'relative_volume', cadence: 60_000, age: 22_000 + instrumentIndex * 4_000 },
      { name: 'regime_snapshot', cadence: 300_000, age: 46_000 + instrumentIndex * 9_000 },
    ].map((feature) => {
      const age = feature.age;
      const status: FeatureFreshnessRow['status'] =
        age > feature.cadence * 2 ? 'stale' : age > feature.cadence * 1.2 ? 'aging' : 'fresh';
      return {
        feature_version: MOCK_FEATURE_VERSION,
        symbol: instrument.symbol,
        feature_name: feature.name,
        computed_at: iso(-age),
        age_ms: age,
        expected_cadence_ms: feature.cadence,
        status: instrumentIndex === 3 && feature.name === 'regime_snapshot' ? 'stale' : status,
      };
    }),
  );
}

export function buildResearchCosts(): ResearchCallCostSummary {
  const assessments = buildResearchAssessments();
  const latencies = assessments.map((assessment) => assessment.call.latency_ms).sort((left, right) => left - right);
  const p95Index = Math.min(latencies.length - 1, Math.floor(latencies.length * 0.95));
  return {
    window_start: iso(-24 * 3_600_000),
    window_end: iso(0),
    calls: assessments.length,
    failures: assessments.filter((assessment) => assessment.call.status !== 'ok').length,
    total_prompt_tokens: assessments.reduce((total, assessment) => total + assessment.call.prompt_tokens, 0),
    total_completion_tokens: assessments.reduce((total, assessment) => total + assessment.call.completion_tokens, 0),
    total_cost_usd: round(assessments.reduce((total, assessment) => total + assessment.call.cost_usd, 0), 4),
    average_latency_ms: latencies.length === 0 ? null : Math.round(latencies.reduce((total, value) => total + value, 0) / latencies.length),
    p95_latency_ms: latencies.length === 0 ? null : (latencies[p95Index] ?? null),
    schema_failure_rate: round(assessments.filter((assessment) => assessment.schema_validation.status !== 'valid').length / Math.max(1, assessments.length), 4),
  };
}

export function buildRegimes(): RegimeSnapshotView[] {
  return MOCK_DATASET.live_regimes;
}

/* ------------------------------------------------------------------ *
 * §3.2 F — Backtests and forward tests
 * ------------------------------------------------------------------ */

function metricsSet(seed: number, scale = 1): BacktestMetrics {
  const trades = 96 + seed * 7;
  const winRate = round(0.47 + (seed % 5) * 0.022, 4);
  return {
    completed_trades: trades,
    win_rate: winRate,
    loss_rate: round(1 - winRate - 0.04, 4),
    breakeven_rate: 0.04,
    profit_factor: round(1.08 + (seed % 4) * 0.09, 3),
    expectancy_per_trade: round((4.2 + seed * 1.7) * scale, 2),
    average_r_multiple: round(0.12 + seed * 0.03, 3),
    max_drawdown: round((420 + seed * 66) * scale, 2),
    average_holding_time_ms: Math.round((42 + seed * 3) * 60_000),
    max_holding_time_ms: Math.round((188 + seed * 6) * 60_000),
    exposure_utilization: round(0.31 + seed * 0.03, 3),
    margin_utilization: round(0.12 + seed * 0.02, 3),
    turnover: round(1_840_000 * scale, 2),
    transaction_costs: round(1_260 * scale, 2),
    modeled_slippage_points: round(1.2 + seed * 0.1, 2),
    actual_slippage_points: round(1.9 + seed * 0.16, 2),
    strategy_uptime_pct: round(0.996 - seed * 0.001, 4),
  };
}

export function buildBacktests(): BacktestRunView[] {
  const kinds: BacktestRunView['kind'][] = ['walk_forward', 'historical_backtest', 'tick_replay', 'paper_forward', 'demo_forward'];
  return kinds.map((kind, index) => {
    const status: BacktestRunView['status'] = index === 4 ? 'running' : 'completed';
    const metrics = metricsSet(index + 1);
    const researchMetrics = metricsSet(index + 1, 1.06);
    const points = 60;
    let equity = DEMO_EQUITY_BASE;
    let highWater = DEMO_EQUITY_BASE;
    const equityCurve = Array.from({ length: points }, (_, pointIndex) => {
      const delta = Math.sin(pointIndex / 5 + index) * 34 + (index % 2 === 0 ? 6 : 4);
      equity = round(equity + delta, 2);
      highWater = Math.max(highWater, equity);
      const drawdown = round(highWater - equity, 2);
      return {
        timestamp: iso(-(points - pointIndex) * 3_600_000),
        marked_equity: equity,
        realized_equity: round(equity - (pointIndex % 7) * 3, 2),
        drawdown,
        drawdown_pct: round(drawdown / highWater, 4),
      };
    });

    return {
      run_id: `bt-2026-09-${String(index + 1).padStart(2, '0')}-${kind}`,
      kind,
      status,
      label: `${kind.replace(/_/g, ' ')} · orb-v1.0.0`,
      created_at: iso(-(index + 2) * 26 * 3_600_000),
      started_at: iso(-(index + 2) * 26 * 3_600_000 + 60_000),
      finished_at: index === 4 ? null : iso(-(index + 2) * 26 * 3_600_000 + 2_400_000),
      parameters: { initial_equity: DEMO_EQUITY_BASE, per_trade_risk_pct: 0.5, daily_loss_cap_pct: DAILY_LOSS_CAP_PCT, entry_threshold: 0.5, allocation_share: 0.4 },
      data_range: {
        start: iso(-(400 - index * 30) * 24 * 3_600_000),
        end: iso(-(index + 1) * 24 * 3_600_000),
        timezone: 'Europe/London',
        provenance: index === 2 ? 'broker tick history, demo server, hash-chunked export' : 'broker M1 bars, demo server, hash-chunked export',
      },
      assumptions: {
        spread_model: 'bid/ask spread series from stored ticks; no mid-price fills',
        commission_model: '7.00 USD per lot round turn',
        swap_model: 'broker swap rates applied at the 21:00 broker rollover',
        slippage_model: index === 2 ? 'worst-case tick replay with stop-exit slippage' : 'fixed 1.2 points per fill plus a spread-crossing penalty',
        fill_model: 'marketable limit fills at the next available price, never at the decision bar close',
        intrabar_model: index === 2 ? 'tick replay removes intrabar ordering ambiguity' : 'M1 OHLC with a documented pessimistic stop-first rule',
      },
      versions: {
        strategy_version: 'orb-v1.0.0',
        feature_version: MOCK_FEATURE_VERSION,
        regime_version: MOCK_REGIME_VERSIONS,
        eligibility_config_hash: 'sha256:elig-cfg-4f10',
        model_version: 'deterministic-rules-1.0',
        cost_model_version: 'cost-model-2026.09.1',
        code_commit: 'mock-commit-0000000',
        config_hash: 'sha256:run-cfg-91ab',
      },
      metrics,
      equity_curve: equityCurve,
      daily_drawdown_distribution: [
        { bucket: '0–0.5%', count: 42 - index },
        { bucket: '0.5–1.0%', count: 29 - index },
        { bucket: '1.0–1.5%', count: 14 },
        { bucket: '1.5–2.0%', count: 6 + index },
        { bucket: '> 2.0%', count: 3 },
      ],
      trades_by_time_of_day: ['08', '09', '10', '11', '13', '14', '15'].map((hour, hourIndex) => ({
        bucket: `${hour}:00`,
        count: 9 + ((hourIndex * 7 + index) % 14),
        net_pnl: round((hourIndex - 2) * 42 + index * 11, 2),
      })),
      pnl_by_symbol: MOCK_INSTRUMENTS.map((instrument, symbolIndex) => ({
        key: instrument.symbol,
        net_pnl: round((symbolIndex - 1) * 180 + index * 44, 2),
        trades: 18 + ((symbolIndex * 5 + index) % 22),
      })),
      pnl_by_regime: ['trending / normal', 'trending / high', 'ranging / low', 'ranging / normal'].map((key, regimeIndex) => ({
        key,
        net_pnl: round((regimeIndex % 2 === 0 ? 1 : -1) * (120 + regimeIndex * 60) + index * 18, 2),
        trades: 12 + ((regimeIndex * 9 + index) % 20),
      })),
      comparison: [
        { mode: 'quant_only', metrics, executed_branch: true },
        { mode: 'quant_plus_research', metrics: researchMetrics, executed_branch: false },
      ],
      repro_artifact: { path: `artifacts/backtests/${kind}-${index + 1}/run.json`, hash: `sha256:${mockId('artifact', index, 10)}` },
      failure_reason: null,
      notes:
        index === 4
          ? 'Demo forward test is running. Historical and forward results are deliberately kept separate; do not pool them (§3.2 F).'
          : 'Mock result. Costs and slippage assumptions are the ones that matter; a result that only survives the optimistic cost model is not evidence.',
    };
  });
}

/* ------------------------------------------------------------------ *
 * §3.2 G — Operations
 * ------------------------------------------------------------------ */

export function buildBridgeStatus(): BridgeStatusView {
  return {
    ea_name: 'IntradayAiBridge.mq5',
    protocol_version: '1.0.0',
    authenticated: true,
    authentication_method: 'hmac_token',
    token_present: true,
    last_authenticated_heartbeat_at: iso(-1_400),
    heartbeat_interval_ms: 1_000,
    missed_heartbeats: 0,
    connected_since: iso(-4 * 3_600_000),
    remote_address: '127.0.0.1:49712',
    sequence_number: 184_291,
    last_reconciliation: {
      performed_at: iso(-4 * 60_000),
      ok: true,
      positions_checked: MOCK_DATASET.positions.length,
      orders_checked: MOCK_DATASET.working_orders.length,
      mismatches: [
        {
          kind: 'stop_not_confirmed_at_broker',
          detail: 'A position has a stop in the application ledger that the broker has not confirmed as resting server-side.',
          ticket: MOCK_DATASET.positions[2]?.position_ticket ?? null,
        },
      ],
    },
  };
}

export function buildTerminalMetadata(): TerminalMetadataView {
  return {
    terminal_build: '4755',
    terminal_company: 'Mock Broker Ltd',
    terminal_path_redacted: 'C:\\***\\terminal64.exe',
    server_name: 'MockBroker-Demo01',
    server_timezone: 'Europe/London',
    account_mode: 'demo',
    margin_mode: 'hedging',
    trade_allowed: true,
    ea_trade_allowed: true,
    symbol_specs: MOCK_INSTRUMENTS.map((instrument) => ({
      symbol: instrument.symbol,
      digits: instrument.digits,
      point: instrument.point,
      volume_min: instrument.volume_min,
      volume_step: instrument.volume_step,
      volume_max: 50,
      contract_size: instrument.contract_size,
      spread_typical_points: instrument.spread_points,
      swap_long: round(-0.42 - instrument.spread_points / 100, 2),
      swap_short: round(0.18 + instrument.spread_points / 200, 2),
    })),
  };
}

export function buildScheduler(): SchedulerView {
  return {
    scheduler_state: 'waiting',
    broker_timezone: 'Europe/London',
    broker_time: iso(0),
    session_open_at: iso(-184 * 60_000),
    no_new_trades_at: iso(96 * 60_000),
    flatten_deadline_at: iso(214 * 60_000),
    session_close_at: iso(229 * 60_000),
    next_task: { name: 'no-new-trades cutoff', at: iso(96 * 60_000) },
    upcoming: [
      { name: 'no-new-trades cutoff', at: iso(96 * 60_000), kind: 'session' },
      { name: 'flatten open positions', at: iso(214 * 60_000), kind: 'flatten' },
      { name: 'daily report generation', at: iso(235 * 60_000), kind: 'report' },
      { name: 'post-session reconciliation', at: iso(240 * 60_000), kind: 'reconcile' },
    ],
    last_report_at: iso(-24 * 3_600_000),
  };
}

export function buildDataHealth(): DataHealthView {
  return {
    database: { reachable: true, latency_ms: 3, migrations_applied: 14, schema_version: '2026.09.4' },
    tick_ingest: { last_tick_at: iso(-1_180), ticks_last_minute: 1_842, gaps_last_hour: 2 },
    bar_aggregation: { last_bar_at: iso(-46_000), symbols_lagging: ['XAUUSD'] },
    deepseek: { health: 'degraded', last_ok_at: iso(-9 * 60_000) },
    disk: { free_bytes: 214_748_364_800, log_dir_bytes: 3_221_225_472 },
  };
}

export function buildReadiness(): ReadinessView {
  const checks: ReadinessCheckView[] = [
    {
      id: 'demo_mode',
      label: 'Demo account mode verified',
      status: 'pass',
      detail: 'Account mode reported as demo and positively verified by the daemon; a non-demo account fails closed.',
      observed_at: iso(-3 * 3_600_000),
      blocking: true,
      remediation: null,
    },
    {
      id: 'bridge_auth',
      label: 'EA bridge authenticated',
      status: 'pass',
      detail: 'Authenticated HMAC heartbeat received 1.4 s ago; sequence 184291.',
      observed_at: iso(-1_400),
      blocking: true,
      remediation: null,
    },
    {
      id: 'database',
      label: 'Database reachable',
      status: 'pass',
      detail: 'PostgreSQL reachable with 3 ms latency; schema 2026.09.4 (14 migrations).',
      observed_at: iso(-800),
      blocking: true,
      remediation: null,
    },
    {
      id: 'clock_drift',
      label: 'Clock drift within tolerance',
      status: 'warn',
      detail: 'Broker/host clock drift is 142 ms, inside the 500 ms warning band but worth watching across DST changes.',
      observed_at: iso(-1_400),
      blocking: false,
      remediation: 'Confirm the host is syncing against a reliable NTP source before the next session.',
    },
    {
      id: 'deepseek_health',
      label: 'DeepSeek research reachable',
      status: 'warn',
      detail: 'One consecutive failure and elevated p95 latency. The order path does not depend on DeepSeek; research is being treated as a neutral adjustment.',
      observed_at: iso(-9 * 60_000),
      blocking: false,
      remediation: 'Verify the API key and rate limits. A degraded research layer must never block execution.',
    },
    {
      id: 'session_state',
      label: 'Session state consistent',
      status: 'pass',
      detail: 'Session is active with the no-new-trades cutoff 96 minutes away and flatten 214 minutes away.',
      observed_at: iso(0),
      blocking: false,
      remediation: null,
    },
  ];

  return {
    as_of: iso(0),
    overall: 'degraded',
    demo_mode: { pass: true, account_mode: 'demo', demo_verified: true, detail: 'Demo account verified at session start.' },
    bridge_auth: { pass: true, authenticated: true, last_heartbeat_at: iso(-1_400), detail: 'HMAC-authenticated heartbeat within the 3-heartbeat window.' },
    database: { pass: true, reachable: true, latency_ms: 3, detail: 'PostgreSQL 16 reachable; execution fails closed if this check fails.' },
    clock_drift: { pass: true, drift_ms: 142, warn_ms: FRESHNESS_THRESHOLDS.clock_drift_warn_ms, fail_ms: FRESHNESS_THRESHOLDS.clock_drift_fail_ms, detail: 'Within tolerance.' },
    deepseek: { pass: false, health: 'degraded', detail: 'Degraded: one consecutive failure. Research output is neutral while degraded.' },
    session_state: { pass: true, phase: 'active', detail: 'Active session; entries permitted until the no-new-trades cutoff.' },
    checks,
    blockers: [],
    commands_enabled: true,
  };
}

/* ------------------------------------------------------------------ *
 * §3.2 C — Market monitor
 * ------------------------------------------------------------------ */

export function buildQuotes(): QuoteRowView[] {
  return MOCK_INSTRUMENTS.map((instrument, index) => {
    const bid = round(instrument.base_price, instrument.digits);
    const ask = round(instrument.base_price + instrument.spread_points * instrument.point, instrument.digits);
    // One deliberately stale quote so the "labels stale data visibly" gate has
    // something to label.
    const ageMs = index === 3 ? 6_400 : 320 + index * 180;
    const spreadPips = round(instrument.spread_points / (instrument.pip / instrument.point), 1);
    return {
      symbol: instrument.symbol,
      bid,
      ask,
      mid: round((bid + ask) / 2, instrument.digits),
      spread_points: instrument.spread_points,
      spread_pips: spreadPips,
      quote_age_ms: ageMs,
      last_tick_at: iso(-ageMs),
      session_volume: 82_000 + index * 14_500,
      stale: ageMs > FRESHNESS_THRESHOLDS.tick_stale_ms,
    };
  });
}

const TIMEFRAME_MS: Record<Timeframe, number> = { M1: 60_000, M5: 300_000, M15: 900_000 };

export function buildBars(symbol: string, timeframe: Timeframe, limit: number): BarView[] {
  const instrument = instrumentBySymbol(symbol);
  const step = TIMEFRAME_MS[timeframe];
  const bars: BarView[] = [];
  const amplitude = instrument.base_price * 0.0012 * (timeframe === 'M1' ? 0.6 : timeframe === 'M5' ? 1 : 1.6);
  let close = instrument.base_price - amplitude * 0.4;
  const start = NOW - limit * step;

  for (let index = 0; index < limit; index += 1) {
    const progress = index / Math.max(1, limit - 1);
    const wave = Math.sin(progress * Math.PI * 3.2) * amplitude;
    const open = close;
    close = instrument.base_price + wave + (index % 5 === 0 ? amplitude * 0.25 : -amplitude * 0.1);
    const high = Math.max(open, close) + amplitude * 0.28;
    const low = Math.min(open, close) - amplitude * 0.28;
    const time = start + index * step;
    bars.push({
      symbol,
      timeframe,
      time: new Date(time - (time % step)).toISOString(),
      open: round(open, instrument.digits),
      high: round(high, instrument.digits),
      low: round(low, instrument.digits),
      close: round(close, instrument.digits),
      tick_volume: 900 + ((index * 137) % 1_400),
      real_volume: null,
      spread_points: instrument.spread_points,
      // The final bar is still forming; §5.2 forbids signalling on it.
      complete: index < limit - 1,
    });
  }
  return bars;
}

export function buildIndicators(symbol: string, timeframe: Timeframe): IndicatorSeriesView[] {
  const bars = buildBars(symbol, timeframe, 120);
  const instrument = instrumentBySymbol(symbol);
  const atr = instrument.base_price * 0.0009;
  const series: IndicatorSeriesView[] = [];

  const vwapPoints = bars.map((bar, index) => ({
    time: bar.time,
    value: round(instrument.base_price + Math.sin(index / 9) * atr * 0.8, instrument.digits),
  }));
  series.push({ symbol, timeframe, indicator: 'vwap', version: 'vwap-1.1.0', points: vwapPoints });

  series.push({
    symbol,
    timeframe,
    indicator: 'atr14',
    version: 'atr-1.0.2',
    points: bars.map((bar, index) => ({ time: bar.time, value: round(atr * (0.8 + Math.abs(Math.sin(index / 11)) * 0.6), instrument.digits) })),
  });

  const smaWindow = 20;
  series.push({
    symbol,
    timeframe,
    indicator: 'sma20',
    version: 'sma-1.0.0',
    points: bars.map((bar, index) => {
      const window = bars.slice(Math.max(0, index - smaWindow + 1), index + 1);
      const average = window.reduce((total, item) => total + item.close, 0) / Math.max(1, window.length);
      return { time: bar.time, value: round(average, instrument.digits) };
    }),
  });

  series.push({
    symbol,
    timeframe,
    indicator: 'session_high',
    version: 'session-extremes-1.0.0',
    points: bars.map((bar) => ({ time: bar.time, value: round(Math.max(...bars.map((item) => item.high)), instrument.digits) })),
  });
  series.push({
    symbol,
    timeframe,
    indicator: 'session_low',
    version: 'session-extremes-1.0.0',
    points: bars.map((bar) => ({ time: bar.time, value: round(Math.min(...bars.map((item) => item.low)), instrument.digits) })),
  });

  return series;
}

/* ------------------------------------------------------------------ *
 * §3.2 B — Decision trail
 * ------------------------------------------------------------------ */

export function buildDecisionTrail(completedTradeId: string): TradeDecisionTrailView | null {
  const artifacts = MOCK_DATASET.decision_artifacts[completedTradeId];
  if (artifacts === undefined) return null;
  const trade = MOCK_DATASET.closed_trades.find((row) => row.completed_trade_id === completedTradeId) ?? null;

  const submittedAt = artifacts.request.submitted_at;
  const fillAt = artifacts.results[0]?.submitted_at ?? null;
  const intentToFill =
    submittedAt !== null && fillAt !== null ? new Date(fillAt).getTime() - new Date(submittedAt).getTime() : null;

  const totalCosts = trade === null ? null : round(trade.commission + trade.swap + trade.fees, 2);

  return {
    completed_trade_id: completedTradeId,
    decision_id: artifacts.signal.decision_id,
    loaded_at: iso(0),
    signal: artifacts.signal,
    regime: artifacts.regime,
    feature_snapshot: artifacts.features,
    research: artifacts.research,
    risk_decision: artifacts.risk,
    // §19.4: under constrained_auto the human step is `auto_approved` with a
    // null operator. The trail renders that explicitly rather than showing an
    // unexplained empty approval box.
    human_approval: artifacts.approval,
    order_request: artifacts.request,
    order_results: artifacts.results,
    broker_deals: artifacts.deals,
    completed_trade: trade,
    post_trade_tags: trade?.tags.map((tag, index) => ({
      tag,
      applied_at: trade.exit_timestamp,
      applied_by: index === 0 ? 'system:post_trade_tagger' : 'operator.ayoub',
      note: index === 0 ? 'Automatic tag from the exit reason and fill quality.' : 'Applied during the daily review.',
    })) ?? [],
    execution_summary: {
      intent_to_fill_ms: intentToFill,
      estimated_slippage_points: artifacts.results[0]?.estimated_slippage_points ?? null,
      actual_slippage_points: artifacts.results[0]?.actual_slippage_points ?? null,
      gross_realized_pnl: trade?.gross_realized_pnl ?? null,
      total_costs: totalCosts,
      net_realized_pnl: trade?.net_realized_pnl ?? null,
    },
  };
}

export function buildSignalBreakdownForDecision(decisionId: string) {
  const entry = Object.values(MOCK_DATASET.decision_artifacts).find(
    (artifacts) => artifacts.signal.decision_id === decisionId,
  );
  return entry?.breakdown ?? null;
}

export { instrumentBySymbol };
